<template>
  <div
    class="InputWrapper"
  >
    <input
      class="Input"
      ref="input"
      :class="
        { 
          focus,
          error,
          password: passwordVisible || type === 'password',
          hasDatalist: !!datalist.length,
          searchMode,
          modifyInput:showModifyButton,
          withClear: showClear,
        }"
      :placeholder="placeholder"
      :id="id"
      :list="listId"
      :type="computedType"
      :disabled="disabled"
      :readonly="readonly || showModifyButton"
      :name="name"
      :value="modelValue"
      :autocomplete="autocomplete"
      @input="handleInput"
      v-bind="$attrs"
    />
    <Clear v-show="showClear" class="InputClear" @click="handleClear" />
    <PwdVisible v-if="showPwdVisible && passwordVisible" class="InputPwdVisible" @click="handlePasswordVisible" />
    <PwdInvisible v-if="showPwdVisible && !passwordVisible" class="InputPwdVisible" @click="handlePasswordVisible" />
    <span v-if="showModifyButton" :class="{ disabledNumAdd }" class="InputNum Add" @click="handleNumAdd">
      <span>+</span>
    </span>
    <span v-if="showModifyButton" class="InputNum Subtract" @click="handleNumSubtract">
      <span>−</span>
    </span>
    <datalist :id="listId" v-if="!!datalist.length" class="datalist">
      <option v-for="data in datalist" :key="data" :value="data" />
    </datalist>
    <button v-if="searchMode" class="SearchButton" aria-label="Search" @click="$emit('search')">
        <Search class="SearchIcon" />
    </button>
  </div>
</template>
  
<script lang="ts">
import { defineComponent, ref, computed, nextTick } from 'vue'
import { Clear, PwdVisible, PwdInvisible, Search } from '@ka-nabellinc/ka-nabell-icons'

export default defineComponent({
  components: {
    Clear,
    PwdVisible,
    PwdInvisible,
    Search
  },
  props: {
    type: {
      type: String,
      default: null,
    },
    placeholder: {
      type: String,
      default: null,
    },
    focus: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    disabledNumAdd: {
      type: Boolean,
      default: false,
    },
    datalist: {
      type: Array as () => string[],
      default: () => [],
    },
    id: {
      type: String,
      default: null,
    },
    error: {
      type: Boolean,
      default: false,
    },
    listId: {
      type: String,
      default: 'myList',
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    autocomplete: {
      type: String as () => 'on' | 'off',
      default: 'on',
    },
    modelValue: {
      type: [String, Number, Object as () => null],
    },
    name: {
      type: String,
      default: null,
    },
    showPassword: {
      type: Boolean,
      default: false
    },
    searchMode: {
      type: Boolean,
      default: false
    },
    showModifyButton: {
      type: Boolean,
      default: false
    },
    emitNullNumber: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'update:modelValue',
    'clear',
    'numAdd',
    'numSubtract',
    'search'
  ],
  setup(props, { emit }) {
    const passwordVisible = ref<boolean>(props.showPassword)
    const input = ref<HTMLInputElement>()

    const showClear = computed(
      () => (
              !props.type ||
              props.type === 'text' ||
              props.type === 'email' ||
              props.type === 'password' ||
              props.searchMode
            ) &&
            props.datalist.length === 0 &&
            !!inputValue.value &&
            !props.disabled
    )

    const showPwdVisible = computed (
      () => props.type === 'password' &&
            !props.disabled
    )

    const inputValue = computed(() =>
      props.modelValue === null ? '' : String(props.modelValue)
    )

    const computedType = computed(() => {
      if (props.type === 'password') {
        return passwordVisible.value ? 'text' : 'password'
      }
      if (props.type === 'searchMode') {
        return 'text'
      }
      return props.type
    })

    const handleClear = () => {
      input.value.value = ''
      emit('update:modelValue', '')
      emit('clear')
    }

    const handleNumAdd = () => {
      if(props.disabled || props.disabledNumAdd) return
      emit('numAdd')
    }

    const handleNumSubtract = () => {
      if(props.disabled) return
      emit('numSubtract')
    }

    const handlePasswordVisible = () => {
      passwordVisible.value = !passwordVisible.value
      const { value } = input.value
      nextTick(() => {
        input.value.value = value
        input.value?.focus()
      })
    }

    const handleInput = ({target}: Event) => {
      if (!target) {
        return
      }

      const value = (target as HTMLInputElement).value

      if (props.type === 'number') {
        const numberValue = !value && props.emitNullNumber ? null : Number(value)
        emit('update:modelValue', numberValue)

        return
      }

      emit('update:modelValue', value)
    }

    return {
      showClear,
      showPwdVisible,
      passwordVisible,
      input,
      computedType,
      handleClear,
      handleNumAdd,
      handleNumSubtract,
      handlePasswordVisible,
      handleInput,
    }
  }
})
</script>
  
<style scoped>
.InputWrapper {
  width: 100%;
  display: flex;
  align-items: center;
  position: relative;
  justify-content: center;
}

.Input {
  box-sizing: border-box;
  background-color: var(--semantic-colors-background-white);
  border-radius: 8px;
  border: 1px solid var(--semantic-colors-border-box);
  width: 100%;
  height: 38px;
  line-height: 150%;
  padding: 8px 12px;
  font-size: 16px;
  font-weight: 400;
  letter-spacing: 0.14px;
  color: var(--semantic-colors-text-black);
}

.Input[type="date"] {
  -webkit-appearance: none;
}

.Input[type="date"]::-webkit-date-and-time-value {
  text-align: left;
}

.Input.withClear {
  padding: 8px 40px 8px 12px;
}

.Input.password {
  padding: 8px 70px 8px 12px;
}

.Input.searchMode{
  padding: 4px 64px 4px 8px;
}

.Input::placeholder {
  color: var(--semantic-colors-text-placefolder);
}

.Input.focus,
.Input:focus {
  outline: 3px solid var(--primitive-colors-system-blue-200);
}

.Input.searchMode.focus,
.Input.searchMode:focus {
  outline: 3px solid var(--primitive-colors-basic-yellow-200);
}

.Input.focus::placeholder,
.Input:focus::placeholder {
  color: transparent;
}

.Input.error {
  border: 1px solid var(--semantic-colors-border-error);
}

.Input:disabled {
  background-color: var(--semantic-colors-background-lightgray);
  color: var(--semantic-colors-text-disabled);
}

.Input.searchMode:disabled {
    background-color: var(--primitive-colors-neutral-white-400-white);
}

.Input[type="password"] {
  font-weight: 700;
  line-height: 160%;
  letter-spacing: 2px;
}

.Input[type="password"]::placeholder {
  font-weight: 400;
  line-height: 150%;
  letter-spacing: 0.14px;
}

.Input[type="number"] {
  text-align: right;
}

.Input[type="number"]::-webkit-inner-spin-button,
.Input[type="number"]::-webkit-outer-spin-button {
    -webkit-appearance: none;
}

.Input[type="number"].modifyInput {
  text-align: center;
  height: 32px;
  padding: 4px 28px;
  -moz-appearance: textfield;
}

.Input[type="number"].modifyInput:disabled {
  background-color: var(--semantic-colors-background-white);
}

.InputClear {
  position: absolute;
  top: 7px;
  right: 12px;
  cursor: pointer;
}

.Input.password + .InputClear {
  right: 40px;
}

.InputPwdVisible {
  position: absolute;
  top: 7px;
  right: 12px;
  cursor: pointer;
}

.InputNum {
  display: inline-block;
  position: absolute;
  box-sizing: border-box;
  background: var(--semantic-colors-background-lightgray);
  border-radius: 30px;
  width: 18px;
  height: 18px;
  color: var(--semantic-colors-text-black);
  font-size: 16px;
  top: 6.5px;
  cursor: pointer;
}

.Add {
  right: 5px;
}

.Subtract {
  left: 5px;
}

.Input:not(:disabled) ~ .InputNum:active {
  background: var(--semantic-colors-background-gray);
}

.InputNum span {
  display: inline-block;
  position: relative;
  box-sizing: border-box;
  font-weight: 400;
  top: -4px;
  left: 5px;
}

.Input:disabled ~ .InputNum {
  color: var(--semantic-colors-text-disabled);
}

.Input.hasDatalist::-webkit-calendar-picker-indicator {
  position: relative;
  top: -5px;
}

.SearchButton {
  border: none;
  padding: 0;
  margin: 0;
  width: 24px;
  height: 24px;
  position: absolute;
  top: 7px;
  right: 8px;
  cursor: pointer;
  background-color: transparent;
  vertical-align: middle;
  display: flex;
  align-items: center;
  justify-content: center;
}

.Input.searchMode + .InputClear {
  right: 36px;
}

.SearchIcon {
  color: var(--primitive-neutral-black-800);
}

.Input:disabled ~ .SearchButton {
    pointer-events: none;
}

.Input:disabled ~ .SearchButton :deep(.SearchIcon > *) {
    fill: var(--primitive-colors-neutral-white-800);
}

.disabledNumAdd {
  background-color: var(--semantic-colors-background-lightgray);
  color: var(--semantic-colors-text-disabled);
  pointer-events: none;
}
</style>
